import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fleetService } from '../../services/fleetService';
import { AlertTriangle } from 'lucide-react';

/**
 * 調度員表單組件 - 用於新增和編輯調度員
 * @param {Object} props
 * @param {Object} props.initialData - 初始表單數據，編輯模式下提供
 * @param {Function} props.onSubmit - 表單提交回調
 * @param {boolean} props.isEditing - 是否為編輯模式
 * @param {boolean} props.isSubmitting - 是否正在提交
 */
const DispatcherForm = ({ initialData = {}, onSubmit, isEditing = false, isSubmitting = false }) => {
    const navigate = useNavigate();
    const [fleets, setFleets] = useState([]);

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        fleet_id: '',
        is_global_admin: false,
        status: 'active',
        work_shift: '',
        can_modify_fare: false,
        can_cancel_orders: true,
        can_extend_waiting_time: true,
        ...initialData
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchFleets();
    }, []);

    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            setFormData({
                ...formData,
                ...initialData
            });
        }
    }, [initialData]);

    const fetchFleets = async () => {
        try {
            const fleetsData = await fleetService.getFleets();
            setFleets(fleetsData);
        } catch (error) {
            console.error('Error fetching fleets:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });

        // 清除該字段的錯誤
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.full_name.trim()) {
            newErrors.full_name = '請輸入姓名';
        }

        if (!formData.email.trim()) {
            newErrors.email = '請輸入電子郵件';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = '請輸入有效的電子郵件地址';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = '請輸入聯絡電話';
        } else if (!/^\d{8,10}$/.test(formData.phone.replace(/[-\s]/g, ''))) {
            newErrors.phone = '請輸入有效的電話號碼';
        }

        if (!isEditing && !formData.password) {
            newErrors.password = '請設定密碼';
        } else if (!isEditing && formData.password.length < 8) {
            newErrors.password = '密碼長度必須至少為8個字符';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            onSubmit(formData);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 基本信息區塊 */}
                <div className="md:col-span-2">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">基本資訊</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
                                姓名 <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="full_name"
                                name="full_name"
                                value={formData.full_name}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 border ${errors.full_name ? 'border-red-300' : 'border-gray-300'} rounded-md`}
                            />
                            {errors.full_name && (
                                <p className="mt-1 text-sm text-red-600">{errors.full_name}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                電子郵件 <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 border ${errors.email ? 'border-red-300' : 'border-gray-300'} rounded-md`}
                            />
                            {errors.email && (
                                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                聯絡電話 <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-300' : 'border-gray-300'} rounded-md`}
                            />
                            {errors.phone && (
                                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="work_shift" className="block text-sm font-medium text-gray-700 mb-1">
                                工作時段
                            </label>
                            <input
                                type="text"
                                id="work_shift"
                                name="work_shift"
                                value={formData.work_shift}
                                onChange={handleChange}
                                placeholder="例：09:00-18:00"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                        {!isEditing && (
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                    密碼 <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password || ''}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border ${errors.password ? 'border-red-300' : 'border-gray-300'} rounded-md`}
                                />
                                {errors.password && (
                                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                                )}
                            </div>
                        )}

                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                                狀態
                            </label>
                            <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="active">在職</option>
                                <option value="inactive">停職</option>
                                <option value="suspended">暫停</option>
                                <option value="on_leave">請假中</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* 車隊與權限區塊 */}
                <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">車隊與權限</h3>

                    <div className="mb-4">
                        <label htmlFor="fleet_id" className="block text-sm font-medium text-gray-700 mb-1">
                            所屬車隊
                        </label>
                        <select
                            id="fleet_id"
                            name="fleet_id"
                            value={formData.fleet_id || ''}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="">全局調度員</option>
                            {fleets.map(fleet => (
                                <option key={fleet.fleet_id} value={fleet.fleet_id}>
                                    {fleet.fleet_name}
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-sm text-gray-500">
                            不選擇車隊則設為全局調度員，可調度全平台訂單
                        </p>
                    </div>

                    <div className="mb-4">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="is_global_admin"
                                name="is_global_admin"
                                checked={formData.is_global_admin}
                                onChange={handleChange}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label htmlFor="is_global_admin" className="ml-2 block text-sm text-gray-700">
                                設為跨車隊管理員
                            </label>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                            擁有跨車隊派單和管理權限
                        </p>
                    </div>
                </div>

                {/* 特殊權限區塊 */}
                <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">特殊權限</h3>

                    <div className="space-y-3">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="can_modify_fare"
                                name="can_modify_fare"
                                checked={formData.can_modify_fare}
                                onChange={handleChange}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label htmlFor="can_modify_fare" className="ml-2 block text-sm text-gray-700">
                                可以修改車資
                            </label>
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="can_cancel_orders"
                                name="can_cancel_orders"
                                checked={formData.can_cancel_orders}
                                onChange={handleChange}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label htmlFor="can_cancel_orders" className="ml-2 block text-sm text-gray-700">
                                可以取消訂單
                            </label>
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="can_extend_waiting_time"
                                name="can_extend_waiting_time"
                                checked={formData.can_extend_waiting_time}
                                onChange={handleChange}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label htmlFor="can_extend_waiting_time" className="ml-2 block text-sm text-gray-700">
                                可以延長等候時間
                            </label>
                        </div>
                    </div>

                    {formData.is_global_admin && (
                        <div className="mt-4 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertTriangle className="h-5 w-5 text-yellow-400" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-yellow-800">注意</h3>
                                    <div className="mt-2 text-sm text-yellow-700">
                                        <p>
                                            跨車隊管理員擁有較高的系統權限，請謹慎分配該權限。
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="border-t border-gray-200 pt-6 flex justify-end space-x-3">
                <button
                    type="button"
                    onClick={() => navigate('/dispatchers')}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    disabled={isSubmitting}
                >
                    取消
                </button>
                <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? '儲存中...' : isEditing ? '更新調度員' : '建立調度員'}
                </button>
            </div>
        </form>
    );
};

export default DispatcherForm;